import { useModal } from "@/features/modal/ModalStackManager";
import styled from "@emotion/styled";
import { DatePicker, DateValue, DatesRangeValue } from "@mantine/dates";
import { Box, Button, Flex, Text } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";

export type CalendarDate = [Date | null, Date | null];

export interface CalendarProps {
  dates: CalendarDate;
  type?: "range" | "default";
  isExclude?: boolean;
  monthOnly?: boolean; // New prop for month-only selection
}

export const isCalendarDate = (value: any): value is CalendarDate => {
  // 첫째, value가 배열인지 확인
  if (!Array.isArray(value) || value.length !== 2) {
    return false;
  }
  // 둘째, 배열의 각 요소가 Date 인스턴스이거나 null인지 확인
  const checkDateOrNull = (item: any) => item === null || item instanceof Date;
  return checkDateOrNull(value[0]) && checkDateOrNull(value[1]);
};

export const Calendar = ({ dates, type = "default", isExclude, monthOnly = false }: CalendarProps) => {
  const { closeModal } = useModal();
  // 단일 날짜 선택 혹은 날짜 범위 선택에 따른 초기 값 설정
  const [value, setValue] = useState<DatesRangeValue | DateValue>(
    type === "range" ? dates : dates[0],
  );

  const handleChange = (selectedDates: DatesRangeValue | DateValue) => {
    setValue(selectedDates);
    
    if (type === "range" && Array.isArray(selectedDates) && selectedDates[1] !== null) {
      closeModal(selectedDates);
    } else if (type === "default" && !Array.isArray(selectedDates) && selectedDates !== null) {
      closeModal([selectedDates, selectedDates]);
    }
  };
  const today = dayjs().startOf("day").toDate();

  // If monthOnly is true, render a custom month picker
  if (monthOnly) {
    return <MonthPicker dates={dates} onSelect={closeModal} />;
  }

  return (
    <MyDatePicker
      type={type === "range" ? "range" : "default"}
      allowSingleDateInRange={type === "range"}
      firstDayOfWeek={0}
      value={value}
      onChange={handleChange}
      size="xl"
      previousIcon={<IconChevronLeft />}
      nextIcon={<IconChevronRight />}
      monthLabelFormat="MM/YYYY"
      excludeDate={
        isExclude
          ? (date) => dayjs(date).isSame(today, "day") || dayjs(date).isAfter(today, "day")
          : undefined
      }
    />
  );
};

// Custom Month Picker Component
interface MonthPickerProps {
  dates: CalendarDate;
  onSelect: (dates: CalendarDate) => void;
}

const MonthPicker = ({ dates, onSelect }: MonthPickerProps) => {
  const initialYear = dates[0] ? dayjs(dates[0]).year() : dayjs().year();
  const initialMonth = dates[0] ? dayjs(dates[0]).month() : null;
  
  const [currentYear, setCurrentYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(initialMonth);

  const months = [
    { value: 0, label: "1월" },
    { value: 1, label: "2월" },
    { value: 2, label: "3월" },
    { value: 3, label: "4월" },
    { value: 4, label: "5월" },
    { value: 5, label: "6월" },
    { value: 6, label: "7월" },
    { value: 7, label: "8월" },
    { value: 8, label: "9월" },
    { value: 9, label: "10월" },
    { value: 10, label: "11월" },
    { value: 11, label: "12월" },
  ];

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    const startDate = dayjs().year(currentYear).month(month).startOf('month').toDate();
    const endDate = dayjs().year(currentYear).month(month).endOf('month').toDate();
    onSelect([startDate, endDate]);
  };

  const handleYearChange = (newYear: number) => {
    setCurrentYear(newYear);
  };

  return (
    <Box p="md" style={{ width: "400px" }}>
      <Flex justify="space-between" align="center" mb="md">
        <Button
          variant="subtle"
          size="sm"
          onClick={() => handleYearChange(currentYear - 1)}
        >
          <IconChevronLeft size={16} />
        </Button>
        <Text size="lg" fw="bold">
          {currentYear}
        </Text>
        <Button
          variant="subtle"
          size="sm"
          onClick={() => handleYearChange(currentYear + 1)}
        >
          <IconChevronRight size={16} />
        </Button>
      </Flex>

      <Box style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
        {months.map((month) => (
          <Button
            key={month.value}
            variant={selectedMonth === month.value ? "filled" : "outline"}
            color={selectedMonth === month.value ? "blue" : "gray"}
            size="sm"
            onClick={() => handleMonthSelect(month.value)}
            style={{ height: "40px" }}
          >
            {month.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

const MyDatePicker = styled(DatePicker)`
  .mantine-DatePicker-monthCell:last-child > button[data-weekend="true"] {
    color: blue;
  }
`;
